import { Text } from '../../primitives/Text.tsx';
import { VisuallyHidden } from '../../primitives/VisuallyHidden.tsx';
import styles from './Chart.module.css';

/**
 * Design system § 14 — Chart, proportion form.
 *
 * One track split into segments, for the question "how does this set divide?".
 * A pie is the same answer in a shape that is harder to read at two
 * categories and impossible at six; this is a bar, and it stays a bar.
 *
 * Segments are separated by a gap in the surface colour rather than by a
 * stroke around each one — a border adds ink that is not data. Each segment
 * is named in the legend beside its own swatch and carries its own count in
 * text, so identity and value are both there for a reader who cannot tell the
 * two hues apart (§ 12).
 *
 * TWO SERIES, in the fixed order § 14 gives them. A third category is a
 * reason to facet the chart, not a reason to invent a hue.
 */

export interface Proportion {
  id: string;
  label: string;
  value: number;
  /** § 3.2 — `chart/series-1` then `chart/series-2`, by entity, never by rank. */
  series: 1 | 2;
}

interface ProportionBarProps {
  title: string;
  segments: readonly Proportion[];
  /** What the counts are of: "contas". */
  itemLabel: string;
  emptyMessage: string;
}

export function ProportionBar({
  title,
  segments,
  itemLabel,
  emptyMessage,
}: ProportionBarProps) {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);

  return (
    <figure className={styles.figure}>
      <figcaption className={styles.caption}>
        <Text as="h3" variant="subheading">
          {title}
        </Text>
      </figcaption>

      {total === 0 ? (
        <Text as="p" variant="body-sm" tone="muted">
          {emptyMessage}
        </Text>
      ) : (
        <>
          <div className={styles.track} aria-hidden="true">
            {segments
              .filter((segment) => segment.value > 0)
              .map((segment) => (
                <span
                  key={segment.id}
                  className={styles.segment}
                  data-series={segment.series}
                  /* The segment's share IS the datum — see ColumnChart. */
                  style={{ flexGrow: segment.value }}
                />
              ))}
          </div>

          {/*
            The legend § 14 requires for two or more series. It carries the
            count as well as the name, so the bar's proportions never have to
            be estimated by eye.
          */}
          <ul className={styles.legend} aria-hidden="true">
            {segments.map((segment) => (
              <li key={segment.id} className={styles.legendItem}>
                <span className={styles.swatch} data-series={segment.series} />
                <Text as="span" variant="body-sm">
                  {segment.label}
                </Text>
                <span className={`${styles.legendValue} type-numeric-inline`}>{segment.value}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      <VisuallyHidden as="div">
        <table>
          <caption>{title}</caption>
          <thead>
            <tr>
              <th scope="col">Grupo</th>
              <th scope="col">{itemLabel}</th>
            </tr>
          </thead>
          <tbody>
            {segments.map((segment) => (
              <tr key={segment.id}>
                <th scope="row">{segment.label}</th>
                <td>{segment.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </VisuallyHidden>
    </figure>
  );
}
